import React from 'react';
import { View, StyleSheet } from 'react-native';
import {
  SkeletonGroup,
  SkeletonBone,
  SkeletonCard,
  SkeletonSection,
  SkeletonList,
  SkeletonListItem,
} from '@/components/common/skeleton';

interface Props {
  isTablet?: boolean;
}

const SECTION_GAP = 24;
const INNER_GAP = 16;

export const DesktopHomeViewSkeleton: React.FC<Props> = ({ isTablet }) => {
  const useStacked = isTablet;

  return (
    <SkeletonGroup style={styles.container}>
      {/* Greeting section */}
      <View style={styles.greetingSection}>
        <SkeletonBone width={200} height={24} />
        <SkeletonBone width={140} height={14} />
      </View>

      {/* Row 1: Balance + Quick Actions */}
      <View style={useStacked ? styles.stackedColumn : styles.twoColumnRow}>
        <View style={styles.columnPrimary}>
          <SkeletonBone width={100} height={18} />
          <SkeletonCard>
            <SkeletonBone width={80} height={14} />
            <SkeletonBone width={120} height={28} />
            <SkeletonBone width={100} height={34} borderRadius={8} />
          </SkeletonCard>
        </View>

        <View style={styles.columnSecondary}>
          <View style={styles.quickActions}>
            {Array.from({ length: 4 }).map((_, i) => (
              <View key={i} style={styles.quickActionItem}>
                <SkeletonBone width={48} height={48} borderRadius={12} />
                <SkeletonBone width={50} height={12} />
              </View>
            ))}
          </View>
        </View>
      </View>

      {/* Row 2: Recent Activity + My Community */}
      <View style={useStacked ? styles.stackedColumn : styles.twoColumnRow}>
        <View style={[styles.columnPrimary, styles.leftColumnGap]}>
          <SkeletonSection headerWidth={120}>
            <SkeletonList count={3} gap={8}>
              <SkeletonListItem avatar avatarSize={36} lines={2} lineWidths={[160, 100]} />
            </SkeletonList>
          </SkeletonSection>

          <SkeletonCard>
            <SkeletonListItem avatar avatarSize={44} lines={2} lineWidths={[140, 100]} />
          </SkeletonCard>
        </View>

        <View style={styles.columnSecondary}>
          <SkeletonSection headerWidth={160}>
            <View style={styles.resourceLinks}>
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonBone key={i} width="100%" height={48} borderRadius={12} />
              ))}
            </View>
          </SkeletonSection>
        </View>
      </View>
    </SkeletonGroup>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: SECTION_GAP,
  },
  greetingSection: {
    gap: 6,
  },
  twoColumnRow: {
    flexDirection: 'row',
    gap: INNER_GAP,
  },
  stackedColumn: {
    gap: INNER_GAP,
  },
  columnPrimary: {
    flex: 3,
  },
  columnSecondary: {
    flex: 2,
  },
  leftColumnGap: {
    gap: INNER_GAP,
  },
  quickActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  quickActionItem: {
    alignItems: 'center',
    gap: 8,
  },
  resourceLinks: {
    gap: 12,
  },
});
